import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {MeshNode} from "#template/nodes/object/mesh/mesh-node"
import type {MeshNodeDefinition, MeshNodeParameters} from "#template/nodes/object/mesh/types"
import {meshNodeParameters} from "#template/nodes/object/mesh/validation"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {meshGeometryAttributeSetLike} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {MeshGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {GenerateMesh} from "#template/runtime-graph/nodes/generate-mesh"
import type {AbstractMeshData} from "#template/interfaces/mesh-data"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {geomToMesh} from "#template/geometry-processing/geometry-graph"
import type {StandardMeshGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import {isStandardMeshAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import {
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    attributeRefSetFromGeometryAttributeRefDataSet,
    collectDisplayGraphResourcesFromGeometryAttributeRefDataSet,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {hashObject, graphToJson} from "@cm/utils"
import {NoopLogger} from "@cm/log"
import {TypeDescriptors as TD} from "#template/runtime-graph/type-descriptors"
import {visitNone} from "#template/template-node/visit-helpers"

const abstractMeshDataDesc = TD.Identity<AbstractMeshData>("contentHash")

function abstractMeshDataFromStandardMeshGeometryAttibuteSetData(meshAttributeSetData: StandardMeshGeometryAttributeSetData): AbstractMeshData {
    const attrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(meshAttributeSetData)
    const displayAttrRefSet = attributeRefSetFromGeometryAttributeRefDataSet(attrRefDataSet, "display")
    const renderAttrRefSet = attributeRefSetFromGeometryAttributeRefDataSet(attrRefDataSet, "render")
    const displayGeometryGraphResources = collectDisplayGraphResourcesFromGeometryAttributeRefDataSet(attrRefDataSet)

    const sortedUVsArray = (uvs: Record<string, AttributeRef>) => {
        const keys = Object.keys(uvs).sort((a, b) => parseInt(a) - parseInt(b))
        return keys.map((key) => uvs[key])
    }

    const displayGeometryGraph = geomToMesh({...displayAttrRefSet, uvs: sortedUVsArray(displayAttrRefSet.uvs)})
    const renderGeometryGraph = geomToMesh({...renderAttrRefSet, uvs: sortedUVsArray(renderAttrRefSet.uvs)})

    return {
        contentHash: hashObject(graphToJson([displayGeometryGraph, renderGeometryGraph], NoopLogger)),
        displayGeometryGraph,
        renderGeometryGraph,
        displayGeometryGraphResources,
    }
}

export type GeometryGraphMeshParameters = {
    name: string
    meshAttributeSet: MeshGeometryAttributeSetLikeNode | null
} & MeshNodeParameters

const definition: MeshNodeDefinition<GeometryGraphMeshParameters> = {
    nodeClass: "GeometryGraphMesh",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                meshAttributeSet: z.nullable(meshGeometryAttributeSetLike),
            }),
            meshNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class GeometryGraphMesh extends MeshNode<GeometryGraphMeshParameters> {
    public override get definition() {
        return definition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<GeometryGraphMeshParameters> {
        if (this.parameters.meshAttributeSet === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<GeometryGraphMeshParameters> {
        const {meshAttributeSet} = this.parameters
        if (meshAttributeSet === null) return skipped

        const {evaluator} = context
        const {templateContext} = evaluator
        const {sceneManager} = templateContext

        const scope = evaluator.getScope(this)

        const deferredMeshAttributeSetData = scope.waitForFinishedModifyingScene(evaluator.evaluate(meshAttributeSet), sceneManager)

        const [meshAttributeSetDataValid, meshAttributeSetDataInvalid] = scope.branch(
            scope.pureLambda(
                deferredMeshAttributeSetData,
                (meshAttributeSetData) => {
                    if (!meshAttributeSetData) return null
                    if (!isStandardMeshAttributeSetData(meshAttributeSetData)) return null
                    return meshAttributeSetData
                },
                "meshAttributeSetData",
            ),
        )

        const abstractMeshData = scope.pureLambda(
            meshAttributeSetDataValid,
            (meshAttributeSetData) => abstractMeshDataFromStandardMeshGeometryAttibuteSetData(meshAttributeSetData),
            "abstractMeshData",
            {
                output: abstractMeshDataDesc,
            },
        )

        const {completeMeshData} = scope.node(GenerateMesh, {
            sceneManager,
            computeDummyReifiedMeshData: false,
            inputMeshData: abstractMeshData,
        })

        this.setupMesh(scope, context, completeMeshData, true, meshAttributeSetDataInvalid)

        return visitNone(this.parameters)
    }
}
